import { createGlobalStore, createStore } from 'hox';
import { useEffect, useState } from 'react';
import { OKXUniversalProvider } from '@okxconnect/universal-provider';
import { ethers } from '@bitbot/web3';
import { CHAIN_ID } from '@bitbot/shared/config';
import { toastError } from '@bitbot/ui-design';

export const [useOKX] = createGlobalStore(() => {
  const [provider, setProvider] = useState<OKXUniversalProvider | null>(
    null,
  );

  const isMobile =
    typeof window !== 'undefined' &&
    !!navigator.userAgent.match(/iPhone|iPad|iPod|Android/i);

  const initProvider = async () => {
    if (!isMobile) return;
    try {
      const okxUniversalProvider = await OKXUniversalProvider.init({
        dappMetaData: {
          name: 'Bitbot',
          icon: 'https://mini-app.bitbot.tg/bitbot-logo.png',
        },
      });
      setProvider(okxUniversalProvider);
    } catch (err) {
      toastError('initProvider error');
      console.error('initProvider error', err);
    }
  };

  const getChain = (chainId: number): string => {
    return `eip155:${chainId}`;
  };

  const getAccounts = async (chainId: number): Promise<string[]> => {
    const request = {
      method: 'eth_accounts',
      chain: getChain(chainId),
    };
    return provider?.request(request, getChain(chainId)) as Promise<
      string[]
    >;
  };

  const connect = async (chainId: number) => {
    if (!provider) throw new Error('provider is not ready');
    const result = await provider?.connect({
      namespaces: {
        eip155: {
          chains: [getChain(chainId)],
          defaultChain: chainId + '',
        },
      },
      sessionConfig: {
        redirect: 'tg://resolve',
      },
    });
    return result;
  };

  const getPublicWeb3Provider = (chainId: number) => {
    let rpcUrl = 'https://mainnet.base.org';
    if (chainId == CHAIN_ID.BASE) {
      rpcUrl = 'https://mainnet.base.org';
    } else if (chainId == 56) {
      rpcUrl = 'https://bsc-dataseed.binance.org/';
    }
    return new ethers.providers.JsonRpcProvider(rpcUrl);
  };

  const sendTransaction = async (
    chainId: number,
    from: string,
    to: string,
    value: string,
    data: string = '0x',
  ) => {
    const params = [
      {
        from: from,
        to: to,
        value: ethers.ethers.BigNumber.from(value).toHexString(),
        data: data,
      },
    ];
    console.log('send transaction params', params);
    const transaction = {
      method: 'eth_sendTransaction',
      params: params,
      chain: getChain(chainId),
    };
    return await provider?.request(transaction, getChain(chainId));
  };

  const sendToken = async (
    chainId: number,
    tokenAddress: string,
    from: string,
    to: string,
    value: string,
  ) => {
    console.log(value);
    const abi = [
      'function transfer(address to, uint256 amount) public returns (bool)',
    ];
    const contract = new ethers.Contract(tokenAddress, abi);
    const data = await contract.populateTransaction.transfer(to, value);
    return await sendTransaction(
      chainId,
      from,
      tokenAddress,
      '0',
      data.data,
    );
  };

  const getBalance = async (address: string, chainId: number) => {
    const web3Provider = getPublicWeb3Provider(chainId);
    return web3Provider.getBalance(address);
  };

  const getAccountFromSession = (
    session: any,
    chainId: number,
  ): string => {
    try {
      const accounts = session?.namespaces?.eip155?.accounts;
      if (!accounts || accounts.length < 1) {
        return '';
      }
      for (const account of accounts) {
        const [eip, chain, address] = account.split(':');
        if (chain == chainId + '') {
          return address;
        }
      }
      return '';
    } catch (err) {
      console.error(`getAccountFromSession error`, session, chainId);
      return '';
    }
  };

  useEffect(() => {
    initProvider();
  }, []);

  return {
    provider,
    connect,
    sendTransaction,
    sendToken,
    getAccounts,
    getAccountFromSession,
    getBalance,
  };
});
